import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { useNavigate } from "react-router-dom";
import { Plus, MessageCircle } from "lucide-react";
import { useUnreadMessages } from "@/lib/useUnreadMessages";

export default function Messages() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [bookings, setBookings] = useState([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [compose, setCompose] = useState({ subject: "", body: "", booking_id: "" });
  const [sending, setSending] = useState(false);
  const { refresh: refreshUnread } = useUnreadMessages();

  const loadThreads = () => api.get("/messages").then((r) => setThreads(r.data));

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    loadThreads();
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
  }, [user, loading, nav]);

  useEffect(() => {
    if (!activeId) return;
    api.get(`/messages/${activeId}`).then((r) => setThread(r.data));
    api.post(`/messages/${activeId}/read`).then(() => refreshUnread()).catch(() => {});
  }, [activeId, refreshUnread]);

  const send = async () => {
    if (!reply.trim()) return;
    await api.post(`/messages/${activeId}/reply`, { body: reply });
    setReply("");
    const { data } = await api.get(`/messages/${activeId}`);
    setThread(data);
    await api.post(`/messages/${activeId}/read`).catch(() => {});
    refreshUnread();
    toast.success("Sent");
  };

  const submitCompose = async () => {
    if (!compose.body.trim() || !compose.subject.trim()) {
      toast.error("Add a subject and a message");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/messages", {
        subject: compose.subject,
        body: compose.body,
        booking_id: compose.booking_id || undefined,
      });
      toast.success("Message sent to the host");
      setComposeOpen(false);
      setCompose({ subject: "", body: "", booking_id: "" });
      await loadThreads();
      setActiveId(data.thread_id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (loading || !user) return <div className="container-bl py-20">Loading…</div>;

  return (
    <div className="container-bl py-10" data-testid="page-messages">
      <Seo title="Messages · B&L Rentals" description="Contact support and message the host directly." />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-heading font-bold text-white">Messages</h1>
          <p className="mt-1 text-sm text-[#AEB9C9]">Direct-message the host anytime. Replies come back here — you'll also get an email.</p>
        </div>

        <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#E2AB20] hover:bg-[#D89B08]" data-testid="new-message-btn">
              <Plus size={16} className="mr-2" /> New message
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Message the host</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {bookings.length > 0 && (
                <div>
                  <Label>About a booking (optional)</Label>
                  <select
                    value={compose.booking_id}
                    onChange={(e) => setCompose({ ...compose, booking_id: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white text-[#010F27]"
                    data-testid="compose-booking"
                  >
                    <option value="">General question — not about a booking</option>
                    {bookings.map((b) => (
                      <option key={b.booking_id} value={b.booking_id}>
                        {b.vehicle_name} · {b.start_date} → {b.end_date}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <Label>Subject</Label>
                <Input
                  value={compose.subject}
                  onChange={(e) => setCompose({ ...compose, subject: e.target.value })}
                  placeholder="Quick question about my rental"
                  data-testid="compose-subject"
                />
              </div>
              <div>
                <Label>Message</Label>
                <Textarea
                  rows={5}
                  value={compose.body}
                  onChange={(e) => setCompose({ ...compose, body: e.target.value })}
                  placeholder="Hi Isaac — I wanted to ask about…"
                  data-testid="compose-body"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={submitCompose}
                disabled={sending}
                className="bg-[#E2AB20] hover:bg-[#D89B08]"
                data-testid="compose-send"
              >
                {sending ? "Sending…" : "Send to host"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
        <aside className="card-bl p-4 md:col-span-1 h-fit">
          <div className="text-xs uppercase tracking-widest text-[#AEB9C9] mb-3">Threads</div>
          <div className="space-y-2">
            {threads.length === 0 && (
              <div className="text-sm text-[#AEB9C9] space-y-3">
                <div>No conversations yet.</div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setComposeOpen(true)}
                  data-testid="empty-new-btn"
                  className="w-full"
                >
                  <MessageCircle size={14} className="mr-2" /> Start a conversation
                </Button>
              </div>
            )}
            {threads.map((t) => (
              <button
                key={t.thread_id}
                onClick={() => setActiveId(t.thread_id)}
                data-testid={`thread-${t.thread_id}`}
                className={`w-full text-left p-3 rounded-md border transition ${activeId === t.thread_id ? "border-[#E2AB20] bg-[#E2AB20]/10" : "border-white/10 hover:border-[#E2AB20]/50"}`}
              >
                <div className="font-medium text-white text-sm truncate">{t.subject}</div>
                <div className="text-xs text-[#AEB9C9] mt-0.5 truncate">{t.customer_name} · {t.status}</div>
              </button>
            ))}
          </div>
        </aside>

        <section className="md:col-span-2">
          {!thread && <div className="card-bl p-10 text-center text-[#AEB9C9]">Select a conversation to view, or start a new one.</div>}
          {thread && (
            <div className="card-bl">
              <div className="p-5 border-b border-white/10">
                <div className="font-heading font-semibold text-white">{thread.subject}</div>
                <div className="text-xs text-[#AEB9C9] mt-1">{thread.customer_name} · {thread.customer_email}</div>
              </div>
              <div className="p-5 space-y-3 max-h-[420px] overflow-y-auto">
                {thread.messages?.map((m, i) => {
                  const isHost = m.sender === "support";
                  const mineIsHost = user?.role === "admin";
                  const mine = isHost ? mineIsHost : !mineIsHost;
                  const label = isHost ? (mineIsHost ? "You (host)" : "Host") : (mineIsHost ? thread.customer_name || "Customer" : "You");
                  return (
                    <div key={i} className={`max-w-[85%] rounded-xl p-3 text-sm ${mine ? "ml-auto bg-[#E2AB20] text-[#010F27]" : "bg-white/5 border border-white/10 text-[#E5EAF2]"}`}>
                      <div className={`text-[10px] uppercase tracking-widest ${mine ? "text-[#010F27]/70" : "text-[#AEB9C9]"}`}>{label}</div>
                      <div className="mt-1 whitespace-pre-line">{m.body}</div>
                    </div>
                  );
                })}
              </div>
              <div className="p-5 border-t border-white/10 space-y-3">
                <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a message…" data-testid="thread-reply" />
                <div className="flex justify-end">
                  <Button onClick={send} className="bg-[#E2AB20] hover:bg-[#D89B08] text-[#010F27] font-semibold" data-testid="thread-send">Send</Button>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
